import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    vat: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateVAT = (vat) => {
    const cleanVat = vat.replace(/[^0-9]/g, '');
    return cleanVat.length === 10; // accepteer 10 cijfers, met of zonder BE-prefix
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user types
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate VAT if present
    if (formData.vat && !validateVAT(formData.vat)) {
      setErrors({
        ...errors,
        vat: t('contact.form.vat_invalid', 'Ongeldig BTW-nummer. Formaat: BE0123.456.789 of 0123456789 (10 cijfers).')
      });
      return;
    }

    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        vat: '',
        service: '',
        message: ''
      });
      setErrors({});
    }, 3000);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  const contactInfo = [
    {
      icon: <FaWhatsapp className="w-6 h-6" />,
      title: t('contact.info.whatsapp.title', 'WhatsApp'),
      content: t('contact.info.whatsapp.content', 'Direct antwoord'),
      subtext: '+32 4 12 34 56 78',
      link: 'https://wa.me/32412345678',
      color: '#25D366',
      highlight: true
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: t('contact.info.email.title', 'Email'),
      content: 'Info@luscious.be',
      subtext: t('contact.info.email.subtext', 'Binnen 24u reactie'),
      link: 'mailto:Info@luscious.be',
      color: '#f97316'
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: t('contact.info.phone.title', 'Telefoon'),
      content: '+32 4 12 34 56 78',
      subtext: t('contact.info.phone.subtext', 'Ma-Vr: 9:00 - 18:00'),
      link: 'tel:+32412345678',
      color: '#3b82f6'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t('contact.info.location.title', 'Locatie'),
      content: 'Antwerpen, België',
      subtext: t('contact.info.location.subtext', 'Op afspraak'),
      link: null,
      color: '#10b981'
    }
  ];

  return (
    <div className="contact">      <SEO 
        title={t('contact.seo_title', 'Contacteer Ons')}
        description={t('contact.seo_desc', 'Neem contact op met Luscious voor een vrijblijvend gesprek of offerte. Wij staan klaar om uw digitale vragen te beantwoorden.')}
        canonical="/contact"
      />      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={headerVariants}
            >
              <h1 className="page-title">
                {t('contact.hero_title_1', 'Laten We')} <br />
                <span className="text-gradient">{t('contact.hero_title_2', 'Samenwerken')}</span>
              </h1>
              <p className="page-subtitle">
                {t('contact.hero_subtitle', 'Klaar om uw digitale ambities waar te maken? Of heeft u gewoon een vraag? Wij staan voor u klaar. Bel, mail of stuur een sms!')}
              </p>
              
              <div className="hero-badges">
                <div className="hero-badge">
                  <span className="badge-dot online"></span>
                  {t('contact.status_badge', 'Nu beschikbaar voor nieuwe projecten')}
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <svg viewBox="0 0 600 500" className="hero-svg">
                <defs>
                  <linearGradient id="chatGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fff7ed" />
                    <stop offset="100%" stopColor="#ffedd5" />
                  </linearGradient>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000000" floodOpacity="0.1" />
                  </filter>
                </defs>

                <circle cx="300" cy="250" r="220" fill="url(#chatGrad)" opacity="0.6" />
                
                {/* Main Chat Bubble */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <path d="M150 150 H 450 A 20 20 0 0 1 470 170 V 330 A 20 20 0 0 1 450 350 H 200 L 130 400 V 170 A 20 20 0 0 1 150 150 Z" fill="white" filter="url(#shadow)" />
                  
                  {/* Chat Header */}
                  <rect x="150" y="150" width="320" height="60" rx="20" fill="#f8fafc" />
                  <circle cx="190" cy="180" r="20" fill="#f97316" />
                  <rect x="230" y="170" width="120" height="8" rx="4" fill="#334155" />
                  <rect x="230" y="185" width="80" height="6" rx="3" fill="#94a3b8" />
                  
                  {/* Chat Messages */}
                  <rect x="170" y="240" width="180" height="40" rx="10" fill="#f1f5f9" />
                  <rect x="240" y="290" width="190" height="40" rx="10" fill="#f97316" opacity="0.1" />
                  <text x="260" y="315" fill="#f97316" fontSize="14" fontWeight="bold">Zeker, laten we bellen!</text>
                </motion.g>

                {/* Floating Elements */}
                <motion.g
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <circle cx="480" cy="120" r="35" fill="#25D366" filter="url(#shadow)" />
                  <foreignObject x="445" y="85" width="70" height="70">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <FaWhatsapp size={40} color="white" />
                    </div>
                  </foreignObject>
                </motion.g>

                <motion.g
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <circle cx="100" cy="300" r="30" fill="#3b82f6" filter="url(#shadow)" />
                  <foreignObject x="70" y="270" width="60" height="60">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <Mail size={32} color="white" />
                    </div>
                  </foreignObject>
                </motion.g>

                <motion.circle cx="520" cy="350" r="8" fill="#f97316" opacity="0.6" 
                  animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <motion.div 
              className="contact-form-wrapper"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="form-header">
                <h2>{t('contact.form.title', 'Stuur een bericht')}</h2>
                <p>{t('contact.form.subtitle', 'Vul het formulier in en we nemen binnen 24 uur contact op.')}</p>
              </div>

              {submitted ? (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="success-icon">
                    <CheckCircle size={48} />
                  </div>
                  <h3>{t('contact.success.title', 'Bericht verzonden!')}</h3>
                  <p>{t('contact.success.message', 'Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.')}</p>
                  <button onClick={() => setSubmitted(false)} className="btn-text">
                    {t('contact.success.btn', 'Nog een bericht sturen')}
                  </button>
                </motion.div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">{t('contact.form.name', 'Naam')}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form.name_ph', 'Uw naam')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">{t('contact.form.email', 'E-mail')}</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form.email_ph', 'uw@email.be')}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">{t('contact.form.phone', 'Telefoon')}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form.phone_ph', '+32 ...')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">{t('contact.form.company', 'Bedrijfsnaam (optioneel)')}</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={t('contact.form.company_ph', 'Uw bedrijf')}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="vat">{t('contact.form.vat', 'BTW-nummer (optioneel)')}</label>
                      <input
                        type="text"
                        id="vat"
                        name="vat"
                        value={formData.vat}
                        onChange={handleChange}
                        className={errors.vat ? 'input-error' : ''}
                        placeholder={t('contact.form.vat_ph', 'BE0123.456.789 of 0123456789')}
                      />
                      {errors.vat && <span className="error-text" style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>{errors.vat}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="service">{t('contact.form.subject', 'Onderwerp')}</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      >
                        <option value="">{t('contact.form.subject_ph', 'Waar gaat het over?')}</option>
                        <option value="new_project">{t('contact.form.subject_opts.new_project', 'Nieuw project starten')}</option>
                        <option value="quote">{t('contact.form.subject_opts.quote', 'Offerte aanvragen')}</option>
                        <option value="question">{t('contact.form.subject_opts.question', 'Algemene vraag')}</option>
                        <option value="partnership">{t('contact.form.subject_opts.partnership', 'Samenwerking')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">{t('contact.form.message', 'Uw bericht')}</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder={t('contact.form.message_ph', 'Vertel ons hoe we u kunnen helpen...')}
                    ></textarea>
                  </div>

                  <motion.button 
                    type="submit" 
                    className="btn btn-primary btn-submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{t('contact.form.submit', 'Verstuur Bericht')}</span>
                    <Send size={18} />
                  </motion.button>
                </form>
              )}
            </motion.div>

            {/* Contact Info Cards */}
            <motion.div 
              className="contact-info-wrapper"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              {contactInfo.map((info, index) => (
                <motion.div 
                  key={index}
                  className={`contact-info-card card ${info.highlight ? 'highlight-card' : ''}`}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="info-icon-wrapper" style={{ backgroundColor: info.highlight ? '#dcfce7' : 'var(--bg-subtle)' }}>
                    <div className="info-icon" style={{ color: info.color }}>
                      {info.icon}
                    </div>
                  </div>
                  <div className="info-content">
                    <h4>{info.title}</h4>
                    <p className="info-main-text">{info.content}</p>
                    <p className="info-sub-text">{info.subtext}</p>
                  </div>
                  {info.link && (
                    <a href={info.link} className="info-link-arrow" aria-label={`Ga naar ${info.title}`}>
                      <ArrowRight size={20} />
                    </a>
                  )}
                </motion.div>
              ))}

              <div className="trust-box">
                <h4>{t('contact.trust.title', 'Waarom contact opnemen?')}</h4>
                <ul className="trust-list">
                  <li>
                    <CheckCircle size={16} className="text-orange" />
                    <span>{t('contact.trust.1', 'Vrijblijvend adviesgesprek')}</span>
                  </li>
                  <li>
                    <CheckCircle size={16} className="text-orange" />
                    <span>{t('contact.trust.2', 'Duidelijke offerte vooraf')}</span>
                  </li>
                  <li>
                    <CheckCircle size={16} className="text-orange" />
                    <span>{t('contact.trust.3', 'Direct contact met de ontwikkelaar')}</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section (Optional Visual) */}
    </div>
  );
};

export default Contact;
